import AttendanceLayout from './AttendanceLayout.vue'
import store from '@/store'
import { getAttendanceIndexRoute } from './attendanceRouteUtils'
// Load punch settings route module for Vuex registrations (punch codes, entry states).
import '@/modules/attendance/punchSettings/routes'

export default {
  name: 'attendance',
  label: 'Attendance',
  link: 'attendance',
  path: 'attendance',
  props: true,
  icon: 'clock',
  description: 'View and manage employee attendance.',
  component: AttendanceLayout,
  breadcrumbLeaf: true,
  require: (state, getters) => {
    return state.permissions.includes('manage_clock_logs') ||
           getters.canAccessManagePunches ||
           state.permissions.includes('edit_punches') ||
           state.permissions.includes('view_reports') ||
           (state.features.includes('discipline') && getters.disciplineEnabled && state.permissions.includes('manage_discipline')) ||
           (state.features.includes('geo') && getters.geoEnabled && state.permissions.includes('manage_clock_logs'))
  },
  children: [
    // 1. Time Cards
    {
      name: 'timecards',
      label: 'Time Cards',
      path: 'timecards',
      component: () => import('@/components/TabPanel.vue'),
      require: (state, _getters) => {
        return state.permissions.includes('edit_punches') || state.permissions.includes('view_reports')
      },
      children: [
        {
          name: 'timecard-editor',
          navLabel: 'Time Card Editor',
          navIcon: 'SquarePen',
          description: 'Review and edit worker time cards day by day.',
          label: 'Editor',
          icon: 'pen',
          title: 'Edit Timecards',
          path: 'editor/:view?',
          component: () => import('@/modules/attendance/timecards/TimeCards.vue')
        },
        {
          name: 'shiftcard-list',
          navLabel: 'Bulk Edit Time Cards',
          navIcon: 'ListChecks',
          description: 'Edit many workers\' time cards at once.',
          label: 'Bulk Edit',
          icon: 'list-check',
          title: 'Bulk Edit Timecards',
          path: 'bulk-edit/:view?',
          requireGetter: 'scheduleEnabled',
          component: () => import('@/modules/attendance/timecards/ShiftCardList.vue')
        },
        {
          name: 'shift-extend-requests',
          navIcon: 'Hourglass',
          description: 'Approve or deny workers\' requests to extend their shifts.',
          label: 'Extend Requests',
          icon: 'hourglass-half',
          title: 'Shift Extend Requests',
          path: 'shift-extend-requests/:view?',
          requirePerm: 'edit_punches',
          require: (state) => state.enableShiftExtendRequests,
          component: () => import('@/modules/attendance/timecards/shift-extend/ShiftExtendRequests.vue')
        },
        {
          name: 'timecards-index',
          path: '',
          redirect: { name: 'timecard-editor' }
        }
      ]
    },
    // 2. Raw Punches
    {
      name: 'punches',
      label: 'Raw Punches',
      path: 'punches',
      component: () => import('@/components/TabPanel.vue'),
      requireGetter: 'canAccessManagePunches',
      children: [
        {
          name: 'punch-list',
          navIcon: 'Fingerprint',
          description: 'Every clock-in and clock-out punch as recorded.',
          label: 'List',
          icon: 'list',
          title: 'Raw Punches',
          path: 'list/:view?',
          component: () => import('@/modules/attendance/punches/PunchList.vue')
        },
        {
          name: 'missed-punch-list',
          navIcon: 'TriangleAlert',
          description: 'Shifts missing a clock-in or clock-out.',
          label: 'Missed',
          icon: 'triangle-exclamation',
          title: 'Missed Punches',
          path: 'missed-punches/:view?',
          component: () => import('@/modules/attendance/punches/missedpunch/MissedPunchList.vue'),
          require: state => state.enableSubmitMissedPunch
        },
        {
          name: 'punches-index',
          path: '',
          redirect: { name: 'punch-list' }
        }
      ]
    },
    // 3. Geo Mapping
    {
      name: 'geomap',
      navIcon: 'Map',
      description: 'See where workers clocked in and out on a map.',
      label: 'Geo Mapping',
      title: 'Geo Mapping',
      path: 'geomap',
      component: () => import('@/modules/attendance/geomap/GeoMapping.vue')
        .catch(error => {
          console.error('Failed to route to GeoMapping under attendance', error)
          throw error
        }),
      requireFeature: 'geo',
      requireGetter: 'geoEnabled',
      requirePerm: 'manage_clock_logs',
    },
    // 4. Clock Logs
    {
      name: 'clock-log-list',
      navIcon: 'History',
      description: 'Raw activity from time clock devices and apps.',
      label: 'Clock Logs',
      title: 'Clock Logs',
      path: 'clocklogs/:view?',
      requirePerm: 'manage_clock_logs',
      component: () => import('@/modules/attendance/clocklogs/ClockLogList.vue')
        .catch(error => {
          console.error('Failed to route to ClockLogList under attendance', error)
          throw error
        })
    },
    // 5. Infractions (Discipline)
    {
      name: 'discipline',
      label: 'Infractions',
      path: 'infractions',
      component: () => import('@/components/TabPanel.vue'),
      requirePerm: 'manage_discipline',
      requireFeature: 'discipline',
      requireGetter: 'disciplineEnabled',
      children: [
        {
          name: 'discipline-infractions',
          navIcon: 'ShieldAlert',
          description: 'Late arrivals, absences and other attendance infractions.',
          label: 'Infractions',
          title: 'Attendance Infractions',
          path: 'list/:view?',
          component: () => import('@/modules/discipline/DisciplineInfractionList.vue')
        },
        {
          name: 'discipline-events',
          navIcon: 'Flag',
          description: 'Warnings and other attendance events recorded for workers.',
          label: 'Events',
          title: 'Attendance Events',
          path: 'events/:view?',
          component: () => import('@/modules/discipline/DisciplineEventList.vue')
        },
        {
          name: 'discipline-index',
          path: '',
          redirect: { name: 'discipline-infractions' }
        }
      ]
    },
    // 6. Settings
    {
      name: 'attendance-settings',
      label: 'Settings',
      path: 'settings',
      beforeEnter: to => {
        const hasDiscipline = store.state.features.includes('discipline') && store.getters.disciplineEnabled
        if (to.name === 'attendance-settings') {
          return {
            name: hasDiscipline
              ? 'discipline-defaults'
              : store.getters.hasEntryStateFeature
                ? 'entry-states'
                : store.getters.hasPunchCodeFeature
                  ? 'punch-codes'
                  : 'clock-log-list',
            params: to.params,
            query: to.query,
            hash: to.hash
          }
        }
        return true
      },
      component: () => import('@/components/TabPanel.vue'),
      requirePerm: 'edit_settings',
      require: (state, getters) => {
        return (state.features.includes('discipline') && getters.disciplineEnabled) ||
          getters.hasPunchCodeFeature || getters.hasEntryStateFeature
      },
      children: [
        {
          name: 'discipline-defaults',
          navIcon: 'SlidersHorizontal',
          label: 'Defaults',
          title: 'Attendance Defaults',
          path: 'defaults',
          component: () => import('@/modules/discipline/DisciplineDefaults.vue')
        },
        {
          name: 'discipline-policies',
          navIcon: 'Scale',
          label: 'Policies',
          title: 'Attendance Policies',
          path: 'policies/:view?',
          props: true,
          requireGetter: 'disciplineEnabled',
          component: () => import('@/modules/discipline/DisciplinePolicyList.vue')
        },
        {
          name: 'custom-infraction-types',
          navIcon: 'Tags',
          label: 'Custom Infraction Types',
          title: 'Custom Infraction Types',
          path: 'custom-infraction-types',
          requireGetter: 'disciplineEnabled',
          component: () => import('@/modules/discipline/CustomInfractionTypeList.vue')
        },
        {
          name: 'discipline-event-types',
          navLabel: 'Attendance Event Types',
          navIcon: 'Tag',
          label: 'Event Types',
          title: 'Event Types',
          path: 'event-types',
          requireGetter: 'disciplineEnabled',
          component: () => import('@/modules/discipline/DisciplineEventTypeList.vue')
        },
        {
          name: 'entry-states',
          navIcon: 'Layers',
          label: 'Punch States',
          path: 'states',
          component: () => import('@/modules/attendance/punchSettings/EntryStates.vue'),
          requireFeature: 'entry_state',
        },
        {
          name: 'punch-codes',
          navIcon: 'Hash',
          label: 'Punch Codes',
          path: 'codes',
          component: () => import('@/modules/attendance/punchSettings/PunchCodes.vue'),
          requireFeature: 'punch_codes',
        },
        {
          name: 'attendance-settings-index',
          path: '',
          redirect: _to => ({
            name: (store.state.features.includes('discipline') && store.getters.disciplineEnabled)
              ? 'discipline-defaults'
              : store.getters.hasEntryStateFeature
                ? 'entry-states'
                : 'punch-codes'
          })
        }
      ]
    },
    {
      path: 'events/:view?',
      redirect: to => ({ name: 'discipline-events', params: to.params })
    },
    {
      name: 'attendance-index',
      path: '',
      redirect: () => ({ name: getAttendanceIndexRoute(store.state, store.getters) })
    }
  ]
}
